import "./ContactPage.less";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import { useState } from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import RowContainer from "../../common/RowContainer";
import ColumnContainer from "../../common/ColumnContainer";
import { BoxShadowContainer } from "../../common/BoxShadowContainer";
import { FormattedMessage } from "react-intl";

const ContactPage = () => {
  const theme = useTheme();
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  };

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .trim()
      .min(2, <FormattedMessage id="contacts.form.warnings.min" />)
      .max(
        50,

        <FormattedMessage id="contacts.form.warnings.max" />
      )
      .required(<FormattedMessage id="contacts.form.warnings.empty" />),
    lastName: Yup.string()
      .trim()
      .min(2, <FormattedMessage id="contacts.form.warnings.min" />)
      .max(
        50,

        <FormattedMessage id="contacts.form.warnings.max" />
      )
      .required(<FormattedMessage id="contacts.form.warnings.empty" />),
    email: Yup.string()
      .trim()
      .email(<FormattedMessage id="contacts.form.warnings.email" />)
      .required(<FormattedMessage id="contacts.form.warnings.empty" />),
    message: Yup.string()
      .trim()
      .required(<FormattedMessage id="contacts.form.warnings.empty" />),
  });

  const sendEmail = async (values) => {
    // chiamata asincrona per inviare l'email
    //const response = await fetch("https://api.com/v1/email/send", {
  };

  const handleSubmitContactMessage = async (values, { resetForm }) => {
    try {
      setLoading(true);
      await sendEmail(values);
      setEmailSent(true);
      resetForm();
    } catch (error) {
      setEmailError(true);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: handleSubmitContactMessage,
  });

  return (
    <RowContainer end className="contact-page-container">
      <BoxShadowContainer customWidth="65%">
        <RowContainer>
          <ColumnContainer start>
            <ColumnContainer start>
              <Typography
                variant="h1"
                style={{
                  color: theme.palette.primary.main,
                }}
                gutterBottom
              >
                <FormattedMessage id="contacts.title" />
              </Typography>
              <Typography color="text.primary" fontSize={16}>
                <FormattedMessage id="contacts.subtitle" />
              </Typography>
            </ColumnContainer>
            <RowContainer>
              <ColumnContainer>
                <form noValidate onSubmit={formik.handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={6}>
                      <TextField
                        sx={{ height: 40 }}
                        label={
                          <FormattedMessage id="contacts.form.firstname" />
                        }
                        variant="outlined"
                        color="primary"
                        size="small"
                        name="firstName"
                        fullWidth
                        required
                        value={formik.values.firstName}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.firstName &&
                          Boolean(formik.errors.firstName)
                        }
                        helperText={
                          formik.touched.firstName && formik.errors.firstName
                        }
                        inputProps={{ style: { fontSize: "16px" } }} // font size of input text
                        InputLabelProps={{ style: { fontSize: "16px" } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        sx={{ height: 40 }}
                        label={<FormattedMessage id="contacts.form.lastname" />}
                        variant="outlined"
                        color="primary"
                        size="small"
                        name="lastName"
                        required
                        fullWidth
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.lastName &&
                          Boolean(formik.errors.lastName)
                        }
                        helperText={
                          formik.touched.lastName && formik.errors.lastName
                        }
                        inputProps={{ style: { fontSize: "16px" } }} // font size of input text
                        InputLabelProps={{ style: { fontSize: "16px" } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        sx={{ height: 40 }}
                        label={<FormattedMessage id="contacts.form.email" />}
                        type="email"
                        variant="outlined"
                        color="primary"
                        size="small"
                        name="email"
                        fullWidth
                        required
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.email && Boolean(formik.errors.email)
                        }
                        helperText={formik.touched.email && formik.errors.email}
                        inputProps={{ style: { fontSize: "16px" } }} // font size of input text
                        InputLabelProps={{ style: { fontSize: "16px" } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label={<FormattedMessage id="contacts.form.message" />}
                        multiline
                        rows={4}
                        variant="outlined"
                        color="primary"
                        size="small"
                        name="message"
                        fullWidth
                        required
                        value={formik.values.message}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.message &&
                          Boolean(formik.errors.message)
                        }
                        helperText={
                          formik.touched.message && formik.errors.message
                        }
                        inputProps={{ style: { fontSize: "16px" } }} // font size of input text
                        InputLabelProps={{ style: { fontSize: "16px" } }}
                      />
                    </Grid>
                    <Grid item xs={12} marginTop={"20px"}>
                      <Box>
                        <Typography
                          component="p"
                          variant="body2"
                          align="left"
                          color={theme.palette.text.primary}
                          fontSize={12}
                        >
                          <FormattedMessage id="contacts.form.disclaimer" />{" "}
                          <Box
                            component={Link}
                            to="/privacy-policy"
                            color={theme.palette.primary.main}
                            fontWeight={"700"}
                          >
                            <FormattedMessage id="contacts.form.privacy" />
                          </Box>{" "}
                          <FormattedMessage id="contacts.form.end" />{" "}
                          <Box
                            component={Link}
                            to="/cookies-policy"
                            color={theme.palette.primary.main}
                            fontWeight={"700"}
                          >
                            <FormattedMessage id="contacts.form.cookie" />
                          </Box>
                          .
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <RowContainer align="center">
                        <Button
                          id="submitButton"
                          sx={{ height: 40, minWidth: 150 }}
                          variant="contained"
                          color={"primary"}
                          size="small"
                          type="submit"
                          disabled={!formik.isValid}
                          // disabled={CookieConsent.consented ? false : true}
                        >
                          {loading ? (
                            <CircularProgress color="primary" />
                          ) : emailSent ? (
                            <FormattedMessage id="contacts.form.submitted" />
                          ) : (
                            <FormattedMessage id="contacts.form.submit" />
                          )}
                        </Button>
                        {emailSent ? (
                          <Typography
                            variant={"p"}
                            gutterBottom
                            color={theme.palette.text.primary}
                          >
                            <FormattedMessage id="contacts.form.success" />
                          </Typography>
                        ) : null}
                      </RowContainer>
                    </Grid>
                  </Grid>
                </form>
              </ColumnContainer>
            </RowContainer>
          </ColumnContainer>
        </RowContainer>
      </BoxShadowContainer>
    </RowContainer>
  );
};
export default ContactPage;
